'use client';

import { AutocompleteProvider, useAutocomplete } from '@magicschool/spellbook/components/Autocomplete/AutocompleteProvider';
import type { AutocompleteGroupProps, AutocompleteOptionProps } from '@magicschool/spellbook/components/Autocomplete/slots';
import {
  AutocompleteContentSlot,
  AutocompleteEmptySlot,
  AutocompleteGroupSlot,
  AutocompleteLeadingAddOnSlot,
  AutocompleteLeadingAdornmentSlot,
  AutocompleteOptionSlot,
  AutocompleteSearchOptionSlot,
  AutocompleteSpinnerSlot,
  AutocompleteTrailingAddOnSlot,
  AutocompleteTrailingAdornmentSlot,
  AutocompleteTriggerSlot,
} from '@magicschool/spellbook/components/Autocomplete/slots';
import { Popover } from '@magicschool/spellbook/components/Popover';
import type { SelectProps } from '@magicschool/spellbook/components/Select';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useIsomorphicLayoutEffect } from '@magicschool/spellbook/hooks/useIsomorphicLayoutEffect';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { Children, isValidElement, memo, useMemo, useRef } from 'react';

export interface AutocompleteText {
  /** The default text to display when no results are found. */
  empty?: string;
  /** The label for the search option. */
  searchLabel?: string;
  /** The label for the clear button. */
  clearSearch?: string;
}

export type AutocompleteProps = Omit<SelectProps, 'onBlur' | 'onFocus'> & {
  /** To control behavior while typing a search string, you can use the `onInputChange` prop to handle search string changes and filter options accordingly. */
  onInputChange?: (searchString: string) => void;
  /** Keydown monitoring for 'Enter' key (or optional "Autocomplete.SearchOption" slot) to submit the search string. */
  onInputSubmit?: (searchString: string) => void;
  /** Callback fired when the clear button is clicked. */
  onInputClear?: () => void;
  /** The current value of the input field. */
  inputValue?: string;
  /** Handle the loading state of the options. When `isLoading` is true, a loading spinner is displayed in the dropdown. */
  isLoading?: boolean;
  /** Whether to show the default no results text when no options are found. Set to `false` if you want to handle the no results state yourself, for example when fetching options from an API. */
  disableDefaultNoResults?: boolean;
  /** Set `disableDefaultFiltering` to `true` to disable the default filtering behavior. This is useful when you want to implement your own filtering logic. */
  disableDefaultFiltering?: boolean;
  /** Set `disableTextHighlighting` to `true` to disable the text match highlighting in the options. */
  disableTextHighlighting?: boolean;
  /** Set `disableDefaultSearchOption` to `true` to disable the default search option slot from being used. */
  disableDefaultSearchOption?: boolean;
  /** Callback fired when the input is focused. */
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /** Callback fired when the input is blurred. */
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /** If true, allows freeform text values in the autocomplete input field. */
  allowFreeText?: boolean;
  /** The type of the autocomplete input. Used for styling and behavior. Under the hood, an input type "hidden" is what is always rendered to store the value. */
  type?: 'select' | 'text' | 'search';
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: AutocompleteText;
  /** Controls the open state of the autocomplete dropdown. When provided, the component becomes controlled. */
  open?: boolean;
  /** Whether to apply a shadow effect to the autocomplete trigger. */
  withShadow?: boolean;
  /** If true, automatically focuses the input on mount. */
  autoFocus?: boolean;
};

export const AutocompletePopover: React.FC<
  AutocompleteProps & {
    contentId: string;
    comboboxId: string;
  }
> = memo(
  ({
    className,
    children,
    comboboxId,
    contentId,
    'data-testid': dataTestId,
    defaultOpen,
    descriptionOrientation,
    disabled,
    id,
    invalid,
    labelId,
    name,
    placeholder,
    readOnly,
    size,
    withShadow,
    required,
    'aria-describedby': ariaDescribedBy,
    ...props
  }) => {
    const { slots } = useSlots(children, {
      trigger: {
        Component: AutocompleteTriggerSlot,
        props: {
          required,
          'aria-describedby': ariaDescribedBy,
          readOnly,
          id,
          labelId,
          disabled,
          comboboxId,
          contentId,
          name,
          placeholder,
          size,
          invalid,
          'data-testid': dataTestId,
          children,
          withShadow,
        },
      },
      content: { Component: AutocompleteContentSlot, props: { children, disabled, size } },
    });

    const { open, handleOpenChange, value, currentValue, clearSearchStringIfNoValueMatches } = useAutocomplete();

    if (!slots.trigger) {
      return null;
    }

    return (
      <>
        <Popover defaultOpen={defaultOpen} {...props} open={open} onOpenChange={handleOpenChange} {...props}>
          <Popover.Trigger>{slots.trigger}</Popover.Trigger>
          <Popover.Content
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={clearSearchStringIfNoValueMatches}
            collisionPadding={0}
            role="listbox"
            {...(labelId && { 'aria-labelledby': labelId })}
            id={contentId}
            className={cn('min-w-(--radix-popover-trigger-width) w-full pt-2', className)}
          >
            {slots.content}
          </Popover.Content>
        </Popover>
        {name && <input type="hidden" value={value ?? currentValue} id={id} name={name} readOnly />}
      </>
    );
  },
);

export const AutocompleteBase: React.FC<AutocompleteProps> = ({
  allowFreeText: providedAllowFreeText,
  autoFocus = false,
  'data-testid': dataTestId,
  children,
  className,
  disableDefaultFiltering,
  disableDefaultSearchOption,
  defaultOpen,
  defaultValue,
  descriptionOrientation = 'horizontal',
  disabled = false,
  disableTextHighlighting,
  id: providedId,
  inputValue,
  invalid = false,
  isLoading = false,
  labelId,
  name,
  onBlur,
  onFocus,
  onInputChange,
  onInputClear,
  onInputSubmit,
  onOpenChange,
  onValueChange,
  placeholder,
  readOnly = false,
  disableDefaultNoResults,
  size = 'md',
  text = {},
  type = 'select',
  value: providedValue,
  open: providedOpen,
  withShadow = false,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId(providedId);
  const comboboxId = `${id}-combobox`;
  const contentId = `${id}-content`;

  // Focus synchronously before paint so the input wins over the dialog focus trap, which would
  // otherwise briefly park focus on the first tabbable element (e.g. a leading-adornment button)
  // and flash its tooltip before the input takes focus.
  useIsomorphicLayoutEffect(() => {
    if (inputRef.current && autoFocus && !disabled) {
      inputRef.current.focus();
    }
  }, [autoFocus, disabled]);

  const allowFreeText = useMemo(() => {
    if (providedAllowFreeText !== undefined) {
      return providedAllowFreeText;
    }
    return type === 'text' || type === 'search';
  }, [providedAllowFreeText, type]);

  const initialOptions = useMemo(() => {
    const options = new Map<string, { label: string; value: string }>();

    Children.forEach(children, (child) => {
      if (!isValidElement(child)) {
        return;
      }

      if (child.type === AutocompleteOptionSlot) {
        const props = child.props as AutocompleteOptionProps;
        const { value, label, children: childContent } = props;
        const displayLabel = label || (typeof childContent === 'string' ? childContent : value);
        options.set(value, { value, label: displayLabel });
      } else if (child.type === AutocompleteGroupSlot) {
        const groupProps = child.props as AutocompleteGroupProps;
        if (groupProps.children) {
          Children.forEach(groupProps.children, (groupChild) => {
            if (isValidElement(groupChild) && groupChild.type === AutocompleteOptionSlot) {
              const optionProps = groupChild.props as AutocompleteOptionProps;
              const { value, label, children: childContent } = optionProps;
              const displayLabel = label || (typeof childContent === 'string' ? childContent : value);
              options.set(value, { value, label: displayLabel });
            }
          });
        }
      }
    });
    return options;
  }, [children]);

  return (
    <AutocompleteProvider
      allowFreeText={allowFreeText}
      disableDefaultFiltering={disableDefaultFiltering}
      disableDefaultSearchOption={disableDefaultSearchOption}
      defaultValue={defaultValue}
      descriptionOrientation={descriptionOrientation}
      disabled={disabled}
      disableTextHighlighting={disableTextHighlighting}
      inputRef={inputRef}
      inputValue={inputValue}
      initialOptions={initialOptions}
      isLoading={isLoading}
      text={text}
      onBlur={onBlur}
      onFocus={onFocus}
      onInputChange={onInputChange}
      onInputClear={onInputClear}
      onInputSubmit={onInputSubmit}
      onOpenChange={onOpenChange}
      onValueChange={onValueChange}
      readOnly={readOnly}
      disableDefaultNoResults={disableDefaultNoResults}
      value={providedValue}
      type={type}
      open={providedOpen}
    >
      <AutocompletePopover
        className={className}
        comboboxId={comboboxId}
        contentId={contentId}
        data-testid={dataTestId}
        defaultOpen={defaultOpen}
        descriptionOrientation={descriptionOrientation}
        disabled={disabled}
        id={id}
        invalid={invalid}
        labelId={labelId}
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        size={size}
        withShadow={withShadow}
        {...props}
      >
        {children}
      </AutocompletePopover>
    </AutocompleteProvider>
  );
};

/**
 * A searchable single-select input with typeahead filtering and optional async loading.
 *
 * ### When to use
 * - Lists with 15+ options where typing to filter improves UX
 * - Server-side search (API-fetched options based on user input)
 * - Location pickers, user search, tool search, or any filterable list
 * - When users benefit from seeing matching results as they type
 *
 * ### When NOT to use
 * - For 5-15 static options: use `Select` instead (simpler, no search needed)
 * - For 2-4 visible options: use `RadioGroup` instead (all options visible)
 * - For multiple selections: use `MultiSelect` instead
 * - For command/action menus: use `DropdownMenu` or `Command` instead
 *
 * See the [Select documentation](?path=/docs/components-select--docs) for a detailed comparison of selection components.
 *
 * ### API
 * - `type`: Behavior mode:
 *   - `select` (default): Strict selection from options, chevron icon, clears if no match
 *   - `search`: Freeform text allowed, search icon, shows "Search" option
 *   - `text`: Freeform text allowed, search icon
 * - `value` / `onValueChange`: Controlled selected value
 * - `inputValue` / `onInputChange`: Controlled search text (for external filtering)
 * - `isLoading`: Shows spinner while fetching options
 * - `allowFreeText`: Allow values not in the options list (auto-true for `search`/`text` types)
 * - `disableDefaultFiltering`: Disable built-in filter when using server-side search
 * - `disableDefaultNoResults`: Hide default empty message when handling async states
 *
 * ### Composition (slots)
 * - `<Autocomplete.Option>`: A selectable option. Pass `value` (required), `label`, optional `description`, `disabled`.
 * - `<Autocomplete.Group>`: Groups related options with a label header.
 * - `<Autocomplete.Empty>`: Custom empty/no-results message. Use for async "type to search" prompts.
 * - `<Autocomplete.Spinner>`: Loading indicator (auto-shown when `isLoading={true}`).
 * - `<Autocomplete.SearchOption>`: "Search for..." option (auto-shown for `type="search"`).
 * - `<Autocomplete.LeadingAddOn>`: Content before field with border (e.g., currency label).
 * - `<Autocomplete.LeadingAdornment>`: Icon before the input text.
 * - `<Autocomplete.TrailingAddOn>`: Content after field with border.
 * - `<Autocomplete.TrailingAdornment>`: Icon after input (default: chevron or search based on type).
 *
 * ### Async/server-side search pattern
 * For API-fetched options:
 * - Set `disableDefaultFiltering={true}` — you control filtering
 * - Set `disableDefaultNoResults={true}` — you control empty states
 * - Use `onInputChange` to debounce and fetch options
 * - Use `isLoading={true}` while fetching
 * - Render `<Autocomplete.Empty>` for custom states (min chars, no results)
 *
 * ### Visual states
 * - **Default**: Standard border, chevron/search icon based on type
 * - **Focused/Open**: Focus ring, dropdown visible, text highlighted on match
 * - **Loading**: Spinner in dropdown, options may be filtered
 * - **Empty**: Shows `<Autocomplete.Empty>` or default "No results" message
 * - **Invalid**: Error border color
 * - **Disabled**: Muted background, no interaction
 *
 * ### Accessibility
 * - Uses combobox pattern with `aria-activedescendant` for keyboard navigation
 * - Arrow keys navigate options, Enter selects, Escape closes
 * - Type-ahead highlights matching text in options
 *
 * ### Label association
 * This is an atomic component decoupled from labels. Associate a label using one of:
 * - `labelId`: Pass the ID of an external `<Label>` element
 * - `aria-label`: For visually hidden labels
 * - `id` + `<Label htmlFor={id}>`: Standard HTML label association
 *
 * @headless Composes Popover with no wrapper element; target `autocomplete-trigger` and `autocomplete-content`.
 */
export const Autocomplete = Object.assign(AutocompleteBase, {
  LeadingAddOn: AutocompleteLeadingAddOnSlot,
  LeadingAdornment: AutocompleteLeadingAdornmentSlot,
  TrailingAddOn: AutocompleteTrailingAddOnSlot,
  TrailingAdornment: AutocompleteTrailingAdornmentSlot,
  Option: AutocompleteOptionSlot,
  Group: AutocompleteGroupSlot,
  Trigger: AutocompleteTriggerSlot,
  Content: AutocompleteContentSlot,
  Empty: AutocompleteEmptySlot,
  Spinner: AutocompleteSpinnerSlot,
  SearchOption: AutocompleteSearchOptionSlot,
});
